import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { FaCalendarAlt, FaPen } from "react-icons/fa";
import dayjs from "dayjs";
import styles from "./AdmissionSchedule.module.css";
import {
  CustomButton,
  CustomDatePicker,
  CustomTextInput,
  CustomDropdown,
  CustomTimePicker,
} from "../../../../components";
import AxiosInstance, { TokenService } from "../../../../services/Api";
import PopupModal from "../../../../components/PopupModal/PopupModal";
import CustomMRTTable from "../../../../components/CustomMRTTable/CustomMRTTable";

const ViewAdmissionSchedule = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const editId = location.state?.ID || null;
  const { ID } = location.state || {};

  const { translations } = useSelector((state) => state.language);
  const t = translations?.labels;

  const user = TokenService.getUser();
  const clientData = user?.client_data[0] || {};
  const school_id = clientData?.school_id || "";
  const academic_year = clientData?.academic_year || "";
  const academic_year_id = clientData?.academic_year_id || "";

  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    admissionType: "",
    date: null,
    name: "",
    venue: "",
    fromTime: null,
    toTime: null,
    invite_status: "",
  });
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const [searchInput, setSearchInput] = useState("");
  const [rowCount, setRowCount] = useState(0);

  // Modal Popup
  const [modalState, setModalState] = useState({
    open: false,
    type: "success",
    message: "",
    redirectPath: "",
  });

  useEffect(() => {
    if (editId) {
      fetchInterviewScheduleView();
    }
  }, [editId]);

  const fetchInterviewScheduleView = async () => {
    setLoading(true);
    try {
      const response = await AxiosInstance.get(
        `/admission/interviewSchedule/${editId}`,
      );

      if (response?.data?.status) {
        const list = response.data.data.schedule_list || [];
        const formatted = list.map((item, index) => ({
          sno: index + 1,
          ...item,
        }));
        setData(formatted);
        setRowCount(formatted.length);

        const scheduleData = response.data.data.schedule;
        setFormData({
          admissionType: scheduleData.admission_type || "",
          date: scheduleData.date ? dayjs(scheduleData.date) : null,
          fromTime: scheduleData.start_time
            ? dayjs(scheduleData.start_time, "HH:mm:ss")
            : null,
          toTime: scheduleData.end_time
            ? dayjs(scheduleData.end_time, "HH:mm:ss")
            : null,
          name: scheduleData.name || "",
          venue: scheduleData.interview_venue || "",
          invite_status: scheduleData.invite_status || "",
        });
      }
    } catch (error) {
      console.error("Error fetching interview schedule details:", error);
      setModalState({
        open: true,
        type: "error",
        message: t?.fetchFailed || "Failed to fetch details. Please try again.",
        redirectPath: "",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchSeatAllocatedList = async (admissionType, search = "") => {
    try {
      setLoading(true);

      const payload = {
        admission_type:
          typeof admissionType === "object"
            ? admissionType.label
            : admissionType,
        school_id: school_id,
        search: search,
        academic_year_id: Number(academic_year_id),
      };

      const response = await AxiosInstance.post(
        "/admission/getSeatAllocatedList",
        payload,
      );

      if (response?.data?.status) {
        const list = response.data.data || [];
        const formatted = list.map((item, index) => ({
          sno: index + 1,
          ...item,
        }));
        setData(formatted);
        setRowCount(response.data.pagination?.total || formatted.length);
      } else {
        setData([]);
        setRowCount(0);
      }
    } catch (error) {
      console.error("Seat allocation fetch error:", error);
      setData([]);
      setRowCount(0);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        id: ID,
      };

      const response = await AxiosInstance.post(
        "/admission/interviewInviteSend",
        payload,
      );

      if (response?.data?.status) {
        setModalState({
          open: true,
          type: "success",
          message:
            response.data?.message ||
            t?.interviewInviteSentSuccess ||
            "Interview invite sent successfully!",
          redirectPath: "/user/admission-schedule",
        });
      } else {
        setModalState({
          open: true,
          type: "error",
          message:
            response.data?.message ||
            t?.somethingWentWrong ||
            "Something went wrong!",
          redirectPath: "",
        });
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      setModalState({
        open: true,
        type: "error",
        message: t?.interviewSaveFailed || "Failed to save interview schedule.",
        redirectPath: "",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate("/user/admission-schedule");
  };

  const handleEdit = () => {
    navigate("/user/AdmissionScheduleCreate", {
      state: {
        ID: editId,
      },
    });
  };

  const handleSuccessOk = () => {
    const { type, redirectPath } = modalState;
    setModalState((prev) => ({ ...prev, open: false }));
    if (type === "success" && redirectPath) {
      navigate(redirectPath);
    }
  };

  // Paginate data client-side
  const paginatedData = useMemo(() => {
    const start = pagination.pageIndex * pagination.pageSize;
    const end = start + pagination.pageSize;
    return data.slice(start, end);
  }, [data, pagination.pageIndex, pagination.pageSize]);

  const columns = useMemo(
    () => [
      {
        accessorKey: "sno",
        header: t?.serialNo || "S.No.",
        size: 70,
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        accessorKey: "application_no",
        header: t?.applicationId || "Application ID",
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        accessorKey: "student_name",
        header: t?.name || "Name",
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        accessorKey: "assigned_class",
        header: t?.classLabel || "Class",
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        accessorKey: "assigned_section",
        header: t?.section || "Section",
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        accessorKey: "mobile_number",
        header: t?.mobileNumber || "Mobile Number",
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        accessorKey: "date",
        header: t?.appliedDate || "Applied Date",
        enableSorting: true,
        enableColumnFilter: false,
        Cell: ({ cell }) => {
          const value = cell.getValue();
          return value ? dayjs(value).format("DD-MM-YYYY") : "-";
        },
      },
    ],
    [t],
  );

  if (loading && !data.length) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.headerIcon}>
            <FaCalendarAlt />
          </div>
          <h1 className={styles.headerTitle}>
            {t?.interviewSchedule || "Interview Schedule"}
          </h1>
        </div>
        <div className={styles.loadingContainer}>
          <p>{t?.loading || "Loading..."}</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <PopupModal
        open={modalState.open}
        title={
          modalState.type === "success"
            ? t?.success || "Success"
            : t?.failed || "Failed"
        }
        message={modalState.message}
        onOk={handleSuccessOk}
      />
      <div className={styles.container}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerIcon}>
            <FaCalendarAlt />
          </div>
          <h1 className={styles.headerTitle}>
            {t?.interviewSchedule || "Interview Schedule"}
          </h1>
        </div>

        <div className={styles.actions}>
          <CustomButton
            type="button"
            variant="contained"
            className={styles.addButton}
            fullWidth={false}
            onClick={handleEdit}
            disabled={formData.invite_status === "Sent"}
          >
            <FaPen style={{ marginRight: "8px" }} />
            {t?.edit || "Edit"}
          </CustomButton>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <div className="row">
            {/* Academic Year */}
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomTextInput
                label={t?.academicYear || "Academic Year"}
                name="academicYear"
                type="text"
                value={academic_year}
                disabled
                readOnly
              />
            </div>

            {/* Admission Type */}
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomDropdown
                label={t?.admissionType || "Admission Type"}
                name="admissionType"
                value={formData.admissionType}
                placeholder={t?.selectAdmissionType || "Select admission type"}
                required
                disabled
              />
            </div>

            {/* Name */}
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomTextInput
                label={t?.name || "Name"}
                name="name"
                type="text"
                value={formData.name}
                disabled
                placeholder={t?.enterHere || "Enter here.."}
              />
            </div>

            {/* Date */}
            <div className="col-lg-2 px-2 col-md-6 col-sm-12">
              <CustomDatePicker
                label={t?.date || "Date"}
                name="date"
                value={formData.date}
                placeholder="Ex: 30/07/2016"
                required
                disabled
              />
            </div>

            {/* From Time */}
            <div className="col-lg-2 px-2 col-md-6 col-sm-12">
              <CustomTimePicker
                label={t?.fromTime || "From Time"}
                name="fromTime"
                value={formData.fromTime}
                placeholder="Ex: 09:30"
                required
                disabled
              />
            </div>

            {/* End Time */}
            <div className="col-lg-2 px-2 col-md-6 col-sm-12">
              <CustomTimePicker
                label={t?.toTime || "To Time"}
                name="toTime"
                value={formData.toTime}
                placeholder="Ex: 12:00"
                required
                disabled
              />
            </div>

            {/* Venue */}
            <div className="col-lg-6 px-2 col-md-6 col-sm-12">
              <CustomTextInput
                label={t?.venue || "Venue"}
                name="venue"
                type="text"
                value={formData.venue}
                disabled
                placeholder={t?.enterHere || "Enter here.."}
                required
              />
            </div>
          </div>

          {/* Seat Allocated List Header */}
          <div className={styles.header}>
            <div className={styles.headerIcon}>
              <FaCalendarAlt />
            </div>
            <h1 className={styles.headerTitle}>
              {t?.seatAllocationApplication ||
                "Seat Allocated Application List"}
            </h1>
          </div>

          {/* Table */}
          <CustomMRTTable
            enableRowSelection={false}
            columns={columns}
            data={paginatedData}
            loading={loading}
            pagination={pagination}
            rowCount={rowCount}
            onPaginationChange={setPagination}
            searchInput={searchInput}
            placeholder={t?.searchName || "Search Name"}
            setSearchInput={setSearchInput}
            onSearchEnter={(value) => {
              fetchSeatAllocatedList(formData.admissionType, value);
              setPagination((prev) => ({ ...prev, pageIndex: 0 }));
            }}
            enableColumnFilters={false}
          />

          {/* Buttons */}
          <div className={styles.buttonContainer}>
            <CustomButton
              type="submit"
              variant="contained"
              className={styles.submitButton}
              fullWidth={false}
              loading={submitting}
              disabled={formData.invite_status === "Sent" || submitting}
              loadingText={t?.submitting || "Submitting..."}
            >
              {t?.send || "Send"}
            </CustomButton>

            <CustomButton
              type="button"
              variant="contained"
              className={styles.cancelButton}
              fullWidth={false}
              onClick={handleCancel}
            >
              {t?.cancel || "CANCEL"}
            </CustomButton>
          </div>
        </form>
      </div>
    </>
  );
};

export default ViewAdmissionSchedule;
